"use client"

import React, { useState, useEffect, useMemo, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Tag, Plus, X, Clock, Edit3, Trash2, Search, Save } from "lucide-react"
import {
  getTiposTarifa,
  createTipoTarifa,
  updateTipoTarifa,
  deleteTipoTarifa,
} from "../../services/tipoTarifaService"
import "./TiposTarifa.css"

/** Normaliza una hora que puede llegar como "HH:mm:ss", "HH:mm" o array [h,m] → "HH:mm" (para input time). */
const toTimeInput = (v) => {
  if (v == null || v === "") return ""
  if (Array.isArray(v)) {
    const [h = 0, m = 0] = v
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }
  return String(v).slice(0, 5)
}

/** Muestra la hora en formato 12h AM/PM, o "—" si no hay. */
const formatHora = (v) => {
  const t = toTimeInput(v)
  if (!t) return "—"
  const [hh, mm] = t.split(":")
  const h = parseInt(hh, 10)
  const ampm = h >= 12 ? "PM" : "AM"
  const h12 = h % 12 || 12
  return `${h12}:${mm} ${ampm}`
}

/* ======================= Modal Crear/Editar ======================= */
const TipoModal = ({ tipo, onClose, onSave }) => {
  const isEdit = tipo !== null
  const [form, setForm] = useState({ nombre: "", horaInicio: "", horaFin: "" })
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isEdit) {
      setForm({
        nombre: tipo.nombre ?? "",
        horaInicio: toTimeInput(tipo.horaInicio),
        horaFin: toTimeInput(tipo.horaFin),
      })
    }
  }, [tipo, isEdit])

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    if (!form.nombre.trim()) return setError("El nombre es obligatorio.")
    if (form.horaInicio && form.horaFin && form.horaFin < form.horaInicio) {
      return setError("La hora de fin no puede ser anterior a la de inicio.")
    }
    const payload = {
      nombre: form.nombre.trim(),
      horaInicio: form.horaInicio || null,
      horaFin: form.horaFin || null,
    }
    try {
      setSaving(true)
      await onSave(payload, isEdit ? tipo.id : null)
    } catch (err) {
      setError(err.message || "No se pudo guardar.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tt-modal-overlay" onClick={onClose}>
      <motion.div
        className="tt-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
      >
        <div className="tt-modal-header">
          <h3><Tag size={20} /> {isEdit ? "Editar tipo de tarifa" : "Nuevo tipo de tarifa"}</h3>
          <button className="tt-icon-btn" onClick={onClose} aria-label="Cerrar"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="tt-form">
          <label className="tt-field">
            <span><Tag size={15} /> Nombre</span>
            <input
              name="nombre"
              value={form.nombre}
              onChange={handleChange}
              placeholder="Ej: Hora diurna, Mensual, Nocturno…"
              maxLength={100}
              required
              autoFocus
            />
          </label>

          <div className="tt-row">
            <label className="tt-field">
              <span><Clock size={15} /> Hora inicio <em>(opcional)</em></span>
              <input name="horaInicio" type="time" value={form.horaInicio} onChange={handleChange} />
            </label>
            <label className="tt-field">
              <span><Clock size={15} /> Hora fin <em>(opcional)</em></span>
              <input name="horaFin" type="time" value={form.horaFin} onChange={handleChange} />
            </label>
          </div>
          <p className="tt-hint">Las horas son opcionales: úsalas solo para tipos por horario (ej. diurno/nocturno).</p>

          {error && <div className="tt-error">{error}</div>}

          <div className="tt-modal-actions">
            <button type="button" className="tt-btn tt-btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="tt-btn tt-btn-primary" disabled={saving}>
              <Save size={16} /> {saving ? "Guardando…" : isEdit ? "Guardar" : "Crear"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

/* ============================== Página ============================== */
const TiposTarifa = () => {
  const [tipos, setTipos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [search, setSearch] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [toast, setToast] = useState(null)

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000) }

  const cargar = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const data = await getTiposTarifa()
      setTipos(Array.isArray(data) ? data : [])
    } catch (e) {
      setError(e.message || "Error al cargar los tipos de tarifa.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return tipos
    return tipos.filter((t) => (t.nombre || "").toLowerCase().includes(q))
  }, [tipos, search])

  const handleSave = async (payload, id) => {
    if (id) {
      await updateTipoTarifa(id, payload)
      showToast("✅ Tipo de tarifa actualizado.")
    } else {
      await createTipoTarifa(payload)
      showToast("✅ Tipo de tarifa creado.")
    }
    setModalOpen(false)
    setEditing(null)
    await cargar()
  }

  const handleDelete = async (tipo) => {
    if (!window.confirm(`¿Eliminar el tipo de tarifa "${tipo.nombre}"?\nOjo: si hay tarifas que lo usan, podría fallar.`)) return
    try {
      await deleteTipoTarifa(tipo.id)
      setTipos((prev) => prev.filter((t) => t.id !== tipo.id))
      showToast("🗑️ Tipo de tarifa eliminado.")
    } catch (e) {
      showToast("⚠️ No se pudo eliminar (puede estar en uso por una tarifa).")
    }
  }

  const openNuevo = () => { setEditing(null); setModalOpen(true) }
  const openEditar = (tipo) => { setEditing(tipo); setModalOpen(true) }

  return (
    <div className="tt-page">
      <div className="tt-header">
        <div className="tt-title">
          <Tag size={26} />
          <div>
            <h1>Tipos de Tarifa</h1>
            <p>Gestiona las categorías de tarifa (diurna, nocturna, mensual…)</p>
          </div>
        </div>
        <button className="tt-btn tt-btn-primary" onClick={openNuevo}>
          <Plus size={18} /> Nuevo tipo
        </button>
      </div>

      <div className="tt-toolbar">
        <div className="tt-search">
          <Search size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar tipo de tarifa…"
          />
        </div>
        <span className="tt-count">{filtrados.length} tipo(s)</span>
      </div>

      {error && <div className="tt-error tt-error-block">{error}</div>}

      {loading ? (
        <div className="tt-loading"><div className="tt-spinner" /> Cargando…</div>
      ) : filtrados.length === 0 ? (
        <div className="tt-empty">
          <Tag size={46} />
          <p>No hay tipos de tarifa {search ? "para esa búsqueda" : "registrados"}.</p>
          <button className="tt-btn tt-btn-primary" onClick={openNuevo}><Plus size={16} /> Crear el primero</button>
        </div>
      ) : (
        <div className="tt-grid">
          <AnimatePresence>
            {filtrados.map((tipo) => (
              <motion.div
                key={tipo.id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="tt-card"
              >
                <div className="tt-card-top">
                  <span className="tt-card-icon"><Tag size={18} /></span>
                  <span className="tt-id-badge">#{tipo.id}</span>
                </div>
                <h3 className="tt-card-nombre">{tipo.nombre}</h3>
                <div className="tt-card-horas">
                  <Clock size={14} />
                  {toTimeInput(tipo.horaInicio) || toTimeInput(tipo.horaFin)
                    ? <span>{formatHora(tipo.horaInicio)} — {formatHora(tipo.horaFin)}</span>
                    : <span className="tt-sin-horario">Sin horario específico</span>}
                </div>
                <div className="tt-card-actions">
                  <button className="tt-icon-btn" title="Editar" onClick={() => openEditar(tipo)}><Edit3 size={16} /></button>
                  <button className="tt-icon-btn danger" title="Eliminar" onClick={() => handleDelete(tipo)}><Trash2 size={16} /></button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <AnimatePresence>
        {modalOpen && (
          <TipoModal
            tipo={editing}
            onClose={() => { setModalOpen(false); setEditing(null) }}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div className="tt-toast" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}>
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default TiposTarifa
